import React, { useRef, useState } from "react";
import {
  FiFile,
  FiFileText,
  FiImage,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../BaseUI/Modal";
import Button from "../../BaseUI/Button";
import "./attachmentUpload.css";

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip";

const formatSize = (size) =>
  size < 1024 * 1024
    ? `${Math.max(1, Math.round(size / 1024))} KB`
    : `${(size / 1024 / 1024).toFixed(2)} MB`;

const FileIcon = ({ file }) => {
  const type = file?.type || "";
  if (type.startsWith("image/")) return <FiImage />;
  if (type === "application/pdf") return <FiFileText />;
  return <FiFile />;
};

const ApprovalAttachmentModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  title = "Onayla",
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const close = () => {
    setFile(null);
    setError("");
    setDragActive(false);
    onClose();
  };

  const applyFile = (selected) => {
    if (!selected) return;
    if (selected.size > MAX_SIZE) {
      setFile(null);
      setError("Dosya boyutu en fazla 10 MB olabilir.");
      return;
    }
    setFile(selected);
    setError("");
  };

  const clearFile = () => {
    setFile(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Modal isOpen={isOpen} onClose={close} width="medium">
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        <p className="approval-upload__hint">
          Bu adım için isteğe bağlı bir görsel veya belge ekleyebilirsiniz.
        </p>

        <input
          ref={inputRef}
          type="file"
          className="approval-upload__input"
          accept={ACCEPT}
          onChange={(event) => applyFile(event.target.files?.[0] || null)}
        />

        {file ? (
          <div className="approval-upload__file">
            <span className="approval-upload__file-icon">
              <FileIcon file={file} />
            </span>
            <span className="d-flex flex-column">
              <span className="approval-upload__file-name" title={file.name}>
                {file.name}
              </span>
              <span className="approval-upload__file-meta">
                {formatSize(file.size)}
              </span>
            </span>
            <button
              type="button"
              className="approval-upload__remove"
              title="Dosyayı kaldır"
              onClick={clearFile}
            >
              <FiX />
            </button>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            className={`approval-upload__dropzone${
              dragActive ? " approval-upload__dropzone--active" : ""
            }`}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              applyFile(event.dataTransfer.files?.[0] || null);
            }}
          >
            <span className="approval-upload__dropzone-icon">
              <FiUploadCloud />
            </span>
            <span className="approval-upload__dropzone-title">
              Dosya seçin veya buraya sürükleyin
            </span>
            <span className="approval-upload__dropzone-meta">
              Görsel, PDF, Office, metin veya ZIP · en fazla 10 MB
            </span>
          </div>
        )}

        {error && <p className="approval-upload__error mb-0">{error}</p>}
      </ModalBody>
      <ModalFooter>
        <Button appearance="subtle" onClick={close}>
          İptal
        </Button>
        <Button
          appearance="primary"
          loading={loading}
          disabled={!!error}
          onClick={() => onConfirm(file)}
        >
          Onayla
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ApprovalAttachmentModal;
